import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as ArrowLeft } from '../../../assets/icons/arrow-left.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import ocpiApi from '../../../utils/api/ocpi';
import CpoRoleOperations from '../operations/cpo/CpoRoleOperations';
import EmspRoleOperations from '../operations/emsp/EmspRoleOperations';

/**
 * OCPI Role Operations Page component.
 * @returns {JSX.Element} The rendered component.
 */
export default function OCPIRoleOperations(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation('ocpi');
  const { roleId } = useParams<{ roleId: string }>();

  const [loading, setLoading] = useState(true);
  const [roleData, setRoleData] = useState<any>(null);

  useEffect(() => {
    dispatch(setPageTitle(t('ocpiRoleOperations')));
  }, [dispatch, t]);

  useEffect(() => {
    /**
     * Fetches role data from the backend.
     */
    const fetchRoleData = async () => {
      try {
        const response = await ocpiApi.listTenants();
        const rawData = response.data || {};
        const nestedTenants = Array.isArray(rawData.tenants) ? rawData.tenants : [];
        const tenantsArray = Array.isArray(rawData) ? rawData : nestedTenants;

        const role = tenantsArray.find((tenant: any) => tenant.id === roleId || tenant._id === roleId);
        if (role) {
          setRoleData(role);
        }
      } catch (error) {
        console.error('Failed to fetch roles', error);
      } finally {
        setLoading(false);
      }
    };

    if (roleId) {
      fetchRoleData();
    } else {
      setLoading(false);
    }
  }, [roleId]);

  if (loading) {
    return (
      <div className={`ocpi-stepper-page ${i18n.language === 'ar' ? 'rtl' : ''}`} dir={i18n.dir()}>
        <div className="loader-wrap">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  if (!roleData) {
    return (
      <div className={`ocpi-stepper-page ${i18n.language === 'ar' ? 'rtl' : ''}`} dir={i18n.dir()}>
        <div className="ocpi-role-not-found">
          <h3>{t('roleNotFound')}</h3>
          <p>{t('roleNotFoundDescription')}</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/ev-admin/ocpi')}>
            <ArrowLeft /> {t('goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`ocpi-stepper-page ${i18n.language === 'ar' ? 'rtl' : ''}`} dir={i18n.dir()}>
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="ocpi-page-header">
        <div className="ocpi-page-header__top-row">
          <button
            type="button"
            className={`ocpi-page-header__back-btn ${i18n.language === 'ar' ? 'rtl' : ''}`}
            onClick={() => navigate(-1)}>
            <ArrowLeft />
          </button>
        </div>
        <div className="header-text">
          <h1 className="page-title">{t('ocpiRoleOperations')}</h1>
          <p className="page-description">{t('ocpiRoleOperationsDescription')}</p>
        </div>
      </div>

      <div className="ocpi-role-operations-actions">
        {roleData.role?.toUpperCase() === 'CPO' ? (
          <CpoRoleOperations countryCode={roleData.countryCode} partyId={roleData.partyId} partnerCountryCode={roleData.partnerCountryCode} partnerPartyId={roleData.partnerPartyId} />
        ) : (
          <EmspRoleOperations countryCode={roleData.countryCode} partyId={roleData.partyId} />
        )}
      </div>
    </div>
  );
}
